import { useEffect, useState } from 'react';
import { productService } from '../services/productService';
import publicService from '../services/publicService';
import ProductList from '../components/product/ProductList';
import Loading from '../components/common/Loading';
import HeroSection from '../components/home/HeroSection';
import CategoryShowcase from '../components/home/CategoryShowcase';
import BestSellerSection from '../components/home/BestSellerSection';
import NewArrivalsSection from '../components/home/NewArrivalsSection';
import CampaignStrip from '../components/home/CampaignStrip';
import CampaignBanner from '../components/home/CampaignBanner';
import CampaignSection from '../components/home/CampaignSection';
import Reveal from '../components/common/Reveal';
import SEO from '../components/common/SEO';
import { groupByPlacement } from '../utils/campaign';
import './Home.css';

const Home = () => {
  const [bestSellers, setBestSellers] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [heroBanner, setHeroBanner] = useState(null);
  const [heroProducts, setHeroProducts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        // Fetch data from APIs
        const [
          bestSellersData,
          newArrivalsData,
          featuredData,
          heroBannerData,
          heroProductData,
          campaignData,
        ] = await Promise.all([
          productService.getBestSellers({ limit: 8 }),
          productService.getNewArrivals({ limit: 8 }),
          publicService.getFeaturedProducts(8).catch(() => []),
          publicService.getActiveHeroBanner().catch(() => null),
          // Same reasoning as campaigns: the hero must survive its own
          // content failing to load.
          publicService.getHeroProducts(3).catch(() => []),
          // The whole page is gated on one loading flag, so a campaign
          // failure must never be able to blank the home page.
          publicService.getCampaigns().catch(() => []),
        ]);

        const bestSellersArr = bestSellersData.products || [];
        const newArrivalsArr = newArrivalsData.products || [];

        setBestSellers(bestSellersArr);
        setNewArrivals(newArrivalsArr);
        setHeroBanner(heroBannerData || null);
        setHeroProducts(Array.isArray(heroProductData) ? heroProductData : []);
        setCampaigns(Array.isArray(campaignData) ? campaignData : []);

        // Use featured products from dashboard content if available
        if (
          featuredData &&
          Array.isArray(featuredData) &&
          featuredData.length > 0
        ) {
          setFeaturedProducts(featuredData);
        } else {
          // Fallback: Combine best sellers and new arrivals for featured products
          const featured = [];
          const productIds = new Set();

          // Add best sellers first (up to 4)
          bestSellersArr.slice(0, 4).forEach(product => {
            if (!productIds.has(product.id)) {
              productIds.add(product.id);
              featured.push(product);
            }
          });

          // Add new arrivals (up to 4 more, total 8)
          newArrivalsArr.forEach(product => {
            if (!productIds.has(product.id) && featured.length < 8) {
              productIds.add(product.id);
              featured.push(product);
            }
          });

          setFeaturedProducts(featured);
        }
      } catch (err) {
        setError(err.message);
        // eslint-disable-next-line no-console
        console.error('Error loading home data:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const placements = groupByPlacement(campaigns);
  const heroCampaigns = placements.hero || [];
  const stripCampaigns = placements.strip || [];
  const wideCampaigns = placements.wide || [];
  const sectionCampaigns = placements.section || [];

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return <div className="error-message">Error: {error}</div>;
  }

  return (
    <div className="home">
      <SEO
        title="Premium Watches & Lifestyle in Nepal"
        description="Discover the best collection of premium watches and lifestyle products in Nepal at Essentick. Authentic quality with warranty."
        keywords="watches, Nepal, premium watches, lifestyle, Essentick, authentic watches, online shopping Nepal"
      />
      <HeroSection
        banner={heroBanner}
        campaigns={heroCampaigns}
        heroProducts={heroProducts}
      />

      {stripCampaigns.length > 0 && (
        <Reveal group>
          <CampaignStrip campaigns={stripCampaigns} />
        </Reveal>
      )}

      <Reveal group>
        <CategoryShowcase />
      </Reveal>

      <Reveal group>
        <BestSellerSection products={bestSellers} />
      </Reveal>

      {wideCampaigns.map(campaign => (
        <Reveal group key={campaign.id}>
          <CampaignBanner campaign={campaign} />
        </Reveal>
      ))}

      {featuredProducts.length > 0 && (
        <Reveal
          as="section"
          className="products-section featured-products-section"
        >
          <ProductList products={featuredProducts} title="Featured Products" />
        </Reveal>
      )}

      {sectionCampaigns.map(campaign => (
        <Reveal group key={campaign.id}>
          <CampaignSection campaign={campaign} />
        </Reveal>
      ))}

      {/* New Arrivals Section */}
      <Reveal group>
        <NewArrivalsSection products={newArrivals} />
      </Reveal>
    </div>
  );
};

export default Home;
